import logger from './logger';

/**
 * Enhanced storage utility for PWA authentication persistence
 * Uses IndexedDB as primary storage with localStorage fallback
 * Provides better persistence across PWA app closures on mobile
 */

// IndexedDB configuration
const DB_NAME = 'FeedCowdemyDB';
const DB_VERSION = 1;
const STORE_NAME = 'auth';

interface AuthData {
  key: string;
  value: string;
  timestamp: number;
  expiresAt?: number;
}

class PersistentStorage {
  private db: IDBDatabase | null = null;
  private dbReady: Promise<void>;

  constructor() {
    this.dbReady = this.initDB();
  }

  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        logger.warn('IndexedDB not available, falling back to localStorage only');
        resolve();
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        logger.error('Error opening IndexedDB:', request.error);
        resolve(); // Continue with localStorage fallback
      };

      request.onsuccess = () => {
        this.db = request.result;
        logger.info('IndexedDB initialized successfully');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create auth store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }

  /**
   * Store authentication data with enhanced persistence
   */
  async setItem(key: string, value: string, ttlHours: number = 24 * 7): Promise<void> {
    const authData: AuthData = {
      key,
      value,
      timestamp: Date.now(),
      expiresAt: Date.now() + (ttlHours * 60 * 60 * 1000)
    };

    // Always store in localStorage for immediate access
    try {
      localStorage.setItem(key, value);
      localStorage.setItem(`${key}_meta`, JSON.stringify({
        timestamp: authData.timestamp,
        expiresAt: authData.expiresAt
      }));
    } catch (error) {
      logger.error('Error storing in localStorage:', error);
    }

    // Store in IndexedDB for enhanced persistence
    await this.dbReady;
    if (this.db) {
      try {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        await new Promise<void>((resolve, reject) => {
          const request = store.put(authData);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
        logger.debug(`Stored ${key} in IndexedDB`);
      } catch (error) {
        logger.error('Error storing in IndexedDB:', error);
      }
    }
  }

  /**
   * Retrieve authentication data with fallback strategy
   */
  async getItem(key: string): Promise<string | null> {
    // First check localStorage for immediate access
    let value = localStorage.getItem(key);
    const metaData = localStorage.getItem(`${key}_meta`);
    
    if (value && metaData) {
      try {
        const meta = JSON.parse(metaData);
        // Check if not expired
        if (!meta.expiresAt || Date.now() < meta.expiresAt) {
          return value;
        } else {
          // Expired, remove from localStorage
          this.removeItem(key);
          value = null;
        }
      } catch (error) {
        logger.error('Error parsing metadata:', error);
      }
    }

    // If not in localStorage or expired, check IndexedDB
    await this.dbReady;
    if (this.db && !value) {
      try {
        const transaction = this.db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        
        const authData = await new Promise<AuthData | null>((resolve, reject) => {
          const request = store.get(key);
          request.onsuccess = () => resolve(request.result || null);
          request.onerror = () => reject(request.error);
        });

        if (authData) {
          // Check if not expired
          if (!authData.expiresAt || Date.now() < authData.expiresAt) {
            // Restore to localStorage for quick access
            localStorage.setItem(key, authData.value);
            localStorage.setItem(`${key}_meta`, JSON.stringify({
              timestamp: authData.timestamp,
              expiresAt: authData.expiresAt
            }));
            logger.info(`Restored ${key} from IndexedDB to localStorage`);
            return authData.value;
          } else {
            // Expired, remove from IndexedDB
            this.removeItem(key);
          }
        }
      } catch (error) {
        logger.error('Error retrieving from IndexedDB:', error);
      }
    }

    return value;
  }

  /**
   * Remove authentication data from all storage layers
   */
  async removeItem(key: string): Promise<void> {
    // Remove from localStorage
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_meta`);

    // Remove from IndexedDB
    await this.dbReady;
    if (this.db) {
      try {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        await new Promise<void>((resolve, reject) => {
          const request = store.delete(key);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
        logger.debug(`Removed ${key} from IndexedDB`);
      } catch (error) {
        logger.error('Error removing from IndexedDB:', error);
      }
    }
  }

  /**
   * Clear all authentication data
   */
  async clear(): Promise<void> {
    // Clear authentication-related localStorage items
    const authKeys = ['userToken', 'adminToken', 'userInfo', 'adminUser', 'selectedFarm', 'selectedCorporateFarm', 'refreshToken'];
    authKeys.forEach(key => {
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}_meta`);
    });

    // Clear IndexedDB store
    await this.dbReady;
    if (this.db) {
      try {
        const transaction = this.db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        await new Promise<void>((resolve, reject) => {
          const request = store.clear();
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
        logger.info('Cleared all auth data from IndexedDB');
      } catch (error) {
        logger.error('Error clearing IndexedDB:', error);
      }
    }
  }

  /**
   * Check if storage is available and working
   */
  async isAvailable(): Promise<boolean> {
    await this.dbReady;
    return this.db !== null || typeof Storage !== 'undefined';
  }

  /**
   * Get storage info for debugging
   */
  async getStorageInfo(): Promise<{
    hasIndexedDB: boolean;
    hasLocalStorage: boolean;
    storedKeys: string[];
  }> {
    await this.dbReady;
    
    const storedKeys: string[] = [];
    
    // Check localStorage keys
    if (typeof Storage !== 'undefined') {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.includes('Token') || key.includes('Info') || key.includes('Farm'))) {
          storedKeys.push(key);
        }
      }
    }

    return {
      hasIndexedDB: this.db !== null,
      hasLocalStorage: typeof Storage !== 'undefined',
      storedKeys
    };
  }

  /**
   * Save selected farm for multi-farm users
   */
  saveSelectedFarm(farm: { farmId: string; farmName: string; accountId: string; accountName: string }) {
    return this.setItem('selectedFarm', JSON.stringify(farm), 24 * 7);
  }

  /**
   * Get selected farm for multi-farm users
   */
  async getSelectedFarm() {
    const data = await this.getItem('selectedFarm');
    return data ? JSON.parse(data) : null;
  }
}

// Create singleton instance
const persistentStorage = new PersistentStorage();

export default persistentStorage;
export { persistentStorage };

// Helper functions for easy migration
export const setAuthToken = (token: string, type: 'user' | 'admin') => {
  const key = type === 'admin' ? 'adminToken' : 'userToken';
  return persistentStorage.setItem(key, token, 24 * 7); // 7 days
};

export const getAuthToken = (type: 'user' | 'admin') => {
  const key = type === 'admin' ? 'adminToken' : 'userToken';
  return persistentStorage.getItem(key);
};

export const setUserInfo = (userInfo: object, type: 'user' | 'admin') => {
  const key = type === 'admin' ? 'adminUser' : 'userInfo';
  return persistentStorage.setItem(key, JSON.stringify(userInfo), 24 * 7);
};

export const getUserInfo = async (type: 'user' | 'admin') => {
  const key = type === 'admin' ? 'adminUser' : 'userInfo';
  const data = await persistentStorage.getItem(key);
  return data ? JSON.parse(data) : null;
};

export const setSelectedFarm = (farm: object) => {
  return persistentStorage.setItem('selectedFarm', JSON.stringify(farm), 24 * 7);
};

export const getSelectedFarm = async () => {
  const data = await persistentStorage.getItem('selectedFarm');
  return data ? JSON.parse(data) : null;
};

export const setRefreshToken = (token: string, ttlHours: number = 24 * 30) => {
  return persistentStorage.setItem('refreshToken', token, ttlHours);
};

export const getRefreshToken = () => {
  return persistentStorage.getItem('refreshToken');
};

export const persistSessionTokens = async (
  accessToken: string,
  userType: 'user' | 'admin',
  refreshToken?: string | null
) => {
  await setAuthToken(accessToken, userType);
  if (refreshToken) {
    await setRefreshToken(refreshToken);
  }
};

export const clearAllAuthData = () => {
  return persistentStorage.clear();
};
